import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  ComboboxButton,
} from '@headlessui/react';
import { memo, useState, useMemo, FC } from 'react';
import { Control, Controller, FieldError } from 'react-hook-form';

import { ComboboxOption as OptionType } from '@/components/controls/CustomDropdown';

type ComboboxOptionContentProps = {
  label: string;
  selected: boolean;
};

const ComboboxOptionContent: FC<ComboboxOptionContentProps> = ({
  label,
  selected,
}) => (
  <>
    <span
      className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}
    >
      {label}
    </span>
    {selected && (
      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-honoursOrange">
        <i className="pi pi-check text-sm" />
      </span>
    )}
  </>
);

type CustomComboboxProps = {
  value: string;
  onChange: (value: string | number | boolean) => void;
  selectOptions: OptionType[];
  placeholder: string;
  selectStyle?: string;
  disabled?: boolean;
  searchable?: boolean;
};

const CustomCombobox: FC<CustomComboboxProps> = ({
  value,
  onChange,
  selectOptions,
  placeholder,
  selectStyle,
  disabled = false,
  searchable = true,
}) => {
  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchable) {
      return selectOptions;
    }

    return query === ''
      ? selectOptions
      : selectOptions.filter((option) =>
          option.label.toLowerCase().includes(query.toLowerCase())
        );
  }, [query, selectOptions, searchable]);

  const selectedOption =
    selectOptions.find((opt) => opt.value === value) || null;

  return (
    <Combobox
      value={selectedOption}
      onChange={(option: OptionType) => {
        if (!option) {
          onChange('');
          return;
        }

        onChange(option.value);
      }}
      disabled={disabled}
    >
      <div className="relative">
        <div className="relative">
          <ComboboxInput
            readOnly={!searchable}
            autoComplete="off"
            className={`${selectStyle} ${
              disabled
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed opacity-70'
                : ''
            }`}
            displayValue={(option: OptionType) => option?.label || ''}
            onChange={(event) => {
              if (searchable) {
                setQuery(event.target.value);
              }
            }}
            placeholder={placeholder}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-4">
            <i className="pi pi-chevron-down text-honoursOrange text-xl scale-x-125 scale-y-90" />
          </ComboboxButton>
        </div>

        <ComboboxOptions className="z-10 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
          {filteredOptions.length === 0 && query !== '' ? (
            <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
              Nothing found.
            </div>
          ) : (
            filteredOptions.map((option) => (
              <ComboboxOption
                key={String(option.value)}
                value={option}
                className={({ focus }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 ${
                    focus ? 'bg-honoursOrange text-white' : 'text-gray-900'
                  }`
                }
              >
                {({ selected }) => (
                  <ComboboxOptionContent
                    label={option.label}
                    selected={selected}
                  />
                )}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

type CustomSelectProps = {
  name: string;
  label: string;
  control: Control<any, any>;
  selectOptions: OptionType[];
  placeholder: string;
  divStyle: string;
  labelStyle?: string;
  selectStyle?: string;
  error: FieldError | undefined;
  disabled?: boolean;
  mandatory?: boolean;
  searchable?: boolean;
  onChange?: (value: string | number | boolean) => void;
};

const CustomSelect = memo(
  ({
    name,
    control,
    label,
    selectOptions,
    placeholder,
    divStyle,
    labelStyle,
    selectStyle,
    error,
    disabled = false,
    searchable = true,
    mandatory,
    onChange,
  }: CustomSelectProps) => {
    return (
      <div className={divStyle}>
        <label htmlFor={name} className={labelStyle}>
          <span className="inline-flex items-center">
            {label}
            {mandatory ? <span className="ml-1">*</span> : null}
          </span>
        </label>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <CustomCombobox
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                if (onChange) onChange(value);
              }}
              selectOptions={selectOptions}
              placeholder={placeholder}
              selectStyle={selectStyle}
              disabled={disabled}
              searchable={searchable}
            />
          )}
        />
        {error ? (
          <span className="text-red-600 w-full px-2 text-base text-wrap mt-1">
            {error.message}
          </span>
        ) : null}
      </div>
    );
  }
);

CustomSelect.displayName = 'CustomSelect';

export default CustomSelect;
